import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  Settings,
  Mail,
  MessageSquare,
  Phone,
  Webhook,
  Timer,
  GitBranch,
  Filter,
  Database,
  Zap,
  Save,
  RotateCcw,
  Eye,
  Code,
  User,
  Clock,
  Target,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';
import { WorkflowStep } from '../types/workflow';

interface StepPropertiesPanelProps {
  step: WorkflowStep;
  onUpdate: (updates: Partial<WorkflowStep>) => void;
  onClose: () => void;
}

export const StepPropertiesPanel: React.FC<StepPropertiesPanelProps> = ({
  step,
  onUpdate,
  onClose
}) => {
  const [localStep, setLocalStep] = useState<WorkflowStep>({ ...step });
  const [activeTab, setActiveTab] = useState<'general' | 'config' | 'advanced'>('general');
  const [hasChanges, setHasChanges] = useState(false);

  // Update local step when step prop changes (when different step is selected)
  useEffect(() => {
    setLocalStep({ ...step });
    setHasChanges(false);
    setActiveTab('general'); // Reset to general tab when switching steps
  }, [step]);

  const getStepIcon = (type: string) => {
    switch (type) {
      case 'email':
        return Mail;
      case 'sms':
        return MessageSquare;
      case 'call':
        return Phone;
      case 'webhook':
        return Webhook;
      case 'delay':
        return Timer;
      case 'condition':
        return GitBranch;
      case 'filter':
        return Filter;
      case 'database':
        return Database;
      case 'trigger':
        return Zap;
      default:
        return Settings;
    }
  };

  const getStepColor = (type: string) => {
    const colors = {
      email: 'from-blue-500 to-blue-600',
      sms: 'from-green-500 to-green-600',
      call: 'from-purple-500 to-purple-600',
      webhook: 'from-orange-500 to-orange-600',
      delay: 'from-yellow-500 to-yellow-600',
      condition: 'from-pink-500 to-pink-600',
      filter: 'from-indigo-500 to-indigo-600',
      database: 'from-gray-500 to-gray-600',
      trigger: 'from-red-500 to-red-600'
    };
    return colors[type as keyof typeof colors] || 'from-neutral-500 to-neutral-600';
  };

  const updateLocalStep = useCallback((updates: Partial<WorkflowStep>) => {
    setLocalStep(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(() => {
    onUpdate(localStep);
    setHasChanges(false);
  }, [localStep, onUpdate]);

  const handleReset = useCallback(() => {
    setLocalStep({ ...step });
    setHasChanges(false);
  }, [step]);

  const renderGeneralTab = () => (
    <div className="space-y-4">
      {/* Basic Info */}
      <div>
        <label className="block text-sm font-medium text-neutral-300 mb-2">
          Step Name
        </label>
        <input
          type="text"
          value={localStep.name}
          onChange={(e) => updateLocalStep({ name: e.target.value })}
          className="w-full p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          placeholder="Enter step name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-300 mb-2">
          Description
        </label>
        <textarea
          value={localStep.description || ''}
          onChange={(e) => updateLocalStep({ description: e.target.value })}
          className="w-full h-20 p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
          placeholder="Describe what this step does"
        />
      </div>
    </div>
  );

  const renderConfigTab = () => {
    switch (localStep.type) {
      case 'email':
        return (
          <div className="space-y-4">
            {/* Temporary Email Address Field */}
            <div className="p-3 bg-yellow-900/20 border border-yellow-600/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-yellow-400" />
                <label className="block text-sm font-medium text-yellow-300">
                  *TEMPORARY - SPECIFY EMAIL ADDRESS*
                </label>
              </div>
              <input
                type="email"
                value={localStep.config.tempEmailAddress || ''}
                onChange={(e) => updateLocalStep({
                  config: { ...localStep.config, tempEmailAddress: e.target.value }
                })}
                className="w-full p-3 bg-neutral-800 border border-yellow-600/50 rounded-lg text-white placeholder-neutral-400 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                placeholder="test@example.com"
              />
              <p className="text-xs text-yellow-400 mt-1">
                For development and testing only. This will be replaced with dynamic recipient selection.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">
                Subject
              </label>
              <input
                type="text"
                value={localStep.config.subject || ''}
                onChange={(e) => updateLocalStep({
                  config: { ...localStep.config, subject: e.target.value }
                })}
                className="w-full p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Email subject line"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">
                Email Body
              </label>
              <textarea
                value={localStep.config.body || ''}
                onChange={(e) => updateLocalStep({
                  config: { ...localStep.config, body: e.target.value }
                })}
                className="w-full h-32 p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                placeholder="Email content..."
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-neutral-300">
                  Personalization
                </label>
                <p className="text-xs text-neutral-500 mt-1">
                  Use contact data for personalization
                </p>
              </div>
              <button
                onClick={() => updateLocalStep({
                  config: { ...localStep.config, personalization: !localStep.config.personalization }
                })}
                className={`
                  relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                  ${localStep.config.personalization ? 'bg-blue-600' : 'bg-neutral-600'}
                `}
              >
                <span
                  className={`
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                    ${localStep.config.personalization ? 'translate-x-6' : 'translate-x-1'}
                  `}
                />
              </button>
            </div>
          </div>
        );

      case 'sms':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">
                Message
              </label>
              <textarea
                value={localStep.config.message || ''}
                onChange={(e) => updateLocalStep({
                  config: { ...localStep.config, message: e.target.value }
                })}
                className="w-full h-24 p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                placeholder="SMS message content..."
                maxLength={160}
              />
              <p className="text-xs text-neutral-500 mt-1">
                {(localStep.config.message || '').length}/160 characters
              </p>
            </div>
          </div>
        );

      case 'delay':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">
                Duration
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={localStep.config.duration || 0}
                  onChange={(e) => updateLocalStep({
                    config: { ...localStep.config, duration: parseInt(e.target.value) }
                  })}
                  className="flex-1 p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  min="0"
                />
                <select
                  value={localStep.config.unit || 'seconds'}
                  onChange={(e) => updateLocalStep({
                    config: { ...localStep.config, unit: e.target.value }
                  })}
                  className="p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="seconds">Seconds</option>
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 'webhook':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">
                URL
              </label>
              <input
                type="url"
                value={localStep.config.url || ''}
                onChange={(e) => updateLocalStep({
                  config: { ...localStep.config, url: e.target.value }
                })}
                className="w-full p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="https://api.example.com/webhook"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">
                Method
              </label>
              <select
                value={localStep.config.method || 'POST'}
                onChange={(e) => updateLocalStep({
                  config: { ...localStep.config, method: e.target.value }
                })}
                className="w-full p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-8">
            <Settings className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
            <p className="text-neutral-400 text-sm">
              No specific configuration available for this step type
            </p>
          </div>
        );
    }
  };

  const renderAdvancedTab = () => (
    <div className="space-y-4">
      {/* Position */}
      <div>
        <label className="block text-sm font-medium text-neutral-300 mb-2">
          Position
        </label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-neutral-500 mb-1">X</label>
            <input
              type="number"
              value={localStep.position?.x || 0}
              onChange={(e) => updateLocalStep({
                position: { ...localStep.position, x: parseInt(e.target.value) }
              })}
              className="w-full p-2 bg-neutral-800 border border-neutral-700 rounded text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Y</label>
            <input
              type="number"
              value={localStep.position?.y || 0}
              onChange={(e) => updateLocalStep({
                position: { ...localStep.position, y: parseInt(e.target.value) }
              })}
              className="w-full p-2 bg-neutral-800 border border-neutral-700 rounded text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Timeout Configuration */}
      <div>
        <label className="block text-sm font-medium text-neutral-300 mb-2">
          Timeout (milliseconds)
        </label>
        <input
          type="number"
          value={localStep.timeout || ''}
          onChange={(e) => updateLocalStep({
            timeout: e.target.value ? parseInt(e.target.value) : undefined
          })}
          className="w-full p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          placeholder="No timeout (leave empty)"
          min="0"
        />
        <p className="text-xs text-neutral-500 mt-1">
          Maximum time to wait for step completion. Leave empty for no timeout.
        </p>
      </div>

      {/* Retry Configuration */}
      <div>
        <label className="block text-sm font-medium text-neutral-300 mb-2">
          Retry Configuration
        </label>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Max Attempts</label>
            <input
              type="number"
              value={localStep.retryConfig?.maxAttempts || ''}
              onChange={(e) => {
                const maxAttempts = e.target.value ? parseInt(e.target.value) : undefined;
                if (maxAttempts) {
                  updateLocalStep({
                    retryConfig: {
                      ...localStep.retryConfig,
                      maxAttempts,
                      delay: localStep.retryConfig?.delay || 1000
                    }
                  });
                } else {
                  updateLocalStep({ retryConfig: undefined });
                }
              }}
              className="w-full p-2 bg-neutral-800 border border-neutral-700 rounded text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="No retries"
              min="0"
              max="10"
            />
          </div>
          
          {localStep.retryConfig && (
            <>
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Delay (ms)</label>
                <input
                  type="number"
                  value={localStep.retryConfig.delay || 1000}
                  onChange={(e) => updateLocalStep({
                    retryConfig: {
                      ...localStep.retryConfig!,
                      delay: parseInt(e.target.value) || 1000
                    }
                  })}
                  className="w-full p-2 bg-neutral-800 border border-neutral-700 rounded text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  min="100"
                />
              </div>
              
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Backoff Multiplier</label>
                <input
                  type="number"
                  step="0.1"
                  value={localStep.retryConfig.backoffMultiplier || ''}
                  onChange={(e) => updateLocalStep({
                    retryConfig: {
                      ...localStep.retryConfig!,
                      backoffMultiplier: e.target.value ? parseFloat(e.target.value) : undefined
                    }
                  })}
                  className="w-full p-2 bg-neutral-800 border border-neutral-700 rounded text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="No backoff"
                  min="1"
                  max="10"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Step Configuration */}
      <div>
        <label className="block text-sm font-medium text-neutral-300 mb-2">
          Step Configuration
        </label>
        <div className="bg-neutral-800 rounded-lg p-3 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-neutral-400">Step ID:</span>
            <span className="text-white font-mono text-xs">{localStep.id}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-neutral-400">Step Type:</span>
            <span className="text-white capitalize">{localStep.type}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-neutral-400">Connections:</span>
            <span className="text-white">{localStep.connections.length}</span>
          </div>
          {localStep.timeout && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-400">Timeout:</span>
              <span className="text-white">{localStep.timeout}ms</span>
            </div>
          )}
        </div>
      </div>

      {/* Retry Configuration */}
      {localStep.retryConfig && (
        <div>
          <label className="block text-sm font-medium text-neutral-300 mb-2">
            Retry Configuration
          </label>
          <div className="bg-neutral-800 rounded-lg p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-400">Max Attempts:</span>
              <span className="text-white">{localStep.retryConfig.maxAttempts}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-neutral-400">Delay:</span>
              <span className="text-white">{localStep.retryConfig.delay}ms</span>
            </div>
            {localStep.retryConfig.backoffMultiplier && (
              <div className="flex justify-between text-sm">
                <span className="text-neutral-400">Backoff Multiplier:</span>
                <span className="text-white">{localStep.retryConfig.backoffMultiplier}x</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conditions */}
      {localStep.conditions && localStep.conditions.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-neutral-300 mb-2">
            Conditions ({localStep.conditions.length})
          </label>
          <div className="bg-neutral-800 rounded-lg p-3 space-y-2">
            {localStep.conditions.map((condition, index) => (
              <div key={index} className="text-xs">
                <span className="text-neutral-400">{condition.field}</span>
                <span className="text-blue-400 mx-2">{condition.operator}</span>
                <span className="text-white">{String(condition.value)}</span>
                {condition.logicalOperator && (
                  <span className="text-purple-400 ml-2">{condition.logicalOperator}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const Icon = getStepIcon(localStep.type);

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      className="w-80 h-full bg-neutral-900/90 backdrop-blur-sm border-l border-neutral-800 flex flex-col"
    >
      {/* Header */}
      <div className="p-4 border-b border-neutral-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-gradient-to-r ${getStepColor(localStep.type)}`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Step Properties</h3>
              <p className="text-sm text-neutral-400 capitalize">{localStep.type} Step</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-neutral-400 hover:text-white rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex bg-neutral-800 rounded-lg p-1">
          {[
            { id: 'general', label: 'General', icon: Settings },
            { id: 'config', label: 'Config', icon: Code },
            { id: 'advanced', label: 'Advanced', icon: Target }
          ].map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-neutral-400 hover:text-white'
                  }
                `}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'general' && renderGeneralTab()}
        {activeTab === 'config' && renderConfigTab()}
        {activeTab === 'advanced' && renderAdvancedTab()}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-neutral-800">
        {hasChanges && (
          <div className="mb-3 p-2 bg-yellow-900/20 border border-yellow-800 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-yellow-400">Unsaved changes</span>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={handleReset}
            disabled={!hasChanges}
            className="flex-1 px-4 py-2 bg-neutral-800 text-neutral-300 rounded-lg hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>
    </motion.div>
  );
}; 